import type { UpdateMcpCapabilityRequest, UpdateMcpServerRequest, UpdateRemoteAgentRequest } from '@ralph/contracts'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  type AuthorizeMode,
  applyCapabilities,
  authorizeSystem,
  type BulkCapabilityChange,
  type CreateSystemInput,
  createSystem,
  deleteSystem,
  disconnectMine,
  discoverSystem,
  fetchCapabilities,
  fetchMyConnections,
  fetchRemoteAgents,
  fetchSystem,
  fetchSystems,
  preflightSystem,
  probeSystem,
  updateCapability,
  updateRemoteAgent,
  updateSystem
} from '@/api/systems.ts'
import { engagementKeys } from '@/hooks/use-engagement.ts'
import { makerKeys } from '@/hooks/use-maker.ts'

/**
 * Query hooks for connected systems.
 *
 * Two invalidation rules are worth stating once, because both are the kind that get noticed
 * only as a stale screen.
 *
 * **Anything that changes a capability invalidates the curation backlog.** Enabling one is
 * what puts it in front of an administrator to be given the platform's own words, and the
 * two panels are different tabs of the same studio — a person who enables six capabilities
 * and switches to "Capabilities" expecting six rows and finds none learns not to trust the
 * screen.
 *
 * **And it invalidates the maker's picker.** A capability becomes grantable the moment it is
 * both enabled and curated, so a maker's builder open in another tab is looking at a list
 * that just changed underneath it.
 */

export const systemKeys = {
  all: ['admin', 'mcp', 'systems'] as const,
  one: (id: string) => ['admin', 'mcp', 'systems', id] as const,
  capabilities: (id: string) => ['admin', 'mcp', 'systems', id, 'capabilities'] as const
}

export const useSystems = (enabled: boolean) => useQuery({ queryKey: systemKeys.all, queryFn: fetchSystems, enabled })

export const useSystem = (id: string | undefined) =>
  useQuery({ queryKey: systemKeys.one(id ?? ''), queryFn: () => fetchSystem(id as string), enabled: Boolean(id) })

export const useCapabilities = (id: string | undefined) =>
  useQuery({
    queryKey: systemKeys.capabilities(id ?? ''),
    queryFn: () => fetchCapabilities(id as string),
    enabled: Boolean(id)
  })

/**
 * Preflight is a mutation, not a query, and the distinction is load-bearing.
 *
 * It reaches a third party over the network with a value somebody just typed. A query would
 * be cached, retried and re-fired on window focus — so a mistyped address would be dialled
 * again every time the tab regained focus, and a fixed one would answer from cache. Neither
 * is acceptable for a call whose whole purpose is "what is at this address, right now".
 */
export const usePreflight = () =>
  useMutation({ mutationFn: ({ url, tokenRef }: { url: string; tokenRef?: string }) => preflightSystem(url, tokenRef) })

/**
 * Starting a sign-in is a mutation for the same reason preflight is: it reaches a third
 * party, it must not be cached, and it must not re-fire when the tab regains focus — which
 * for this one would mean minting a second authorization transaction every time somebody
 * alt-tabbed.
 */
export const useAuthorizeSystem = (id: string) =>
  useMutation({
    mutationFn: ({ mode, challengedScope }: { mode: AuthorizeMode; challengedScope?: string }) =>
      authorizeSystem(id, mode, challengedScope)
  })

/**
 * The same two actions, for a system whose id is not known until it exists.
 *
 * The connect flow creates a row and then immediately probes and authorizes it, so the id
 * arrives mid-mutation and cannot be a hook argument. Kept separate rather than making the
 * id optional everywhere, because an `id` that may be undefined is an id that will be
 * undefined somewhere it matters.
 */
export const useProbeAnySystem = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => probeSystem(id),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: systemKeys.all })
    }
  })
}

export const useAuthorizeAnySystem = () =>
  useMutation({
    mutationFn: ({ id, mode, challengedScope }: { id: string; mode: AuthorizeMode; challengedScope?: string }) =>
      authorizeSystem(id, mode, challengedScope)
  })

export const myConnectionKeys = ['me', 'connections'] as const

export const useMyConnections = (enabled = true) =>
  useQuery({ queryKey: myConnectionKeys, queryFn: fetchMyConnections, enabled })

export const useDisconnectMine = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (connectionId: string) => disconnectMine(connectionId),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: myConnectionKeys })
    }
  })
}

export const useCreateSystem = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateSystemInput) => createSystem(input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: systemKeys.all })
    }
  })
}

export const useUpdateSystem = (id: string) => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ version, patch }: { version: number; patch: UpdateMcpServerRequest }) =>
      updateSystem(id, version, patch),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: systemKeys.all })
      void client.invalidateQueries({ queryKey: systemKeys.one(id) })
      // The trust tier gates every risk suggestion, which are computed on read — so moving
      // it changes what the capability list proposes without touching a capability row.
      void client.invalidateQueries({ queryKey: systemKeys.capabilities(id) })
    }
  })
}

export const useDeleteSystem = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteSystem(id),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: systemKeys.all })
      void client.invalidateQueries({ queryKey: makerKeys.capabilities })
    }
  })
}

export const useProbeSystem = (id: string) => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => probeSystem(id),
    onSuccess: () => {
      // The probe writes health back onto the row, so the list is stale the moment it returns.
      void client.invalidateQueries({ queryKey: systemKeys.all })
      void client.invalidateQueries({ queryKey: systemKeys.one(id) })
    }
  })
}

export const useDiscoverSystem = (id: string) => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => discoverSystem(id),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: systemKeys.all })
      void client.invalidateQueries({ queryKey: systemKeys.one(id) })
      void client.invalidateQueries({ queryKey: systemKeys.capabilities(id) })
      void client.invalidateQueries({ queryKey: engagementKeys.curationQueue })
      void client.invalidateQueries({ queryKey: makerKeys.capabilities })
    }
  })
}

const invalidateCapability = (client: ReturnType<typeof useQueryClient>, systemId: string) => {
  void client.invalidateQueries({ queryKey: systemKeys.capabilities(systemId) })
  void client.invalidateQueries({ queryKey: systemKeys.all })
  void client.invalidateQueries({ queryKey: engagementKeys.curationQueue })
  void client.invalidateQueries({ queryKey: makerKeys.capabilities })
}

export const useUpdateCapability = (systemId: string) => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ capabilityId, patch }: { capabilityId: string; patch: UpdateMcpCapabilityRequest }) =>
      updateCapability(capabilityId, patch),
    onSuccess: () => invalidateCapability(client, systemId)
  })
}

export const useApplyCapabilities = (systemId: string) => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (changes: BulkCapabilityChange[]) => applyCapabilities(changes),
    onSuccess: () => invalidateCapability(client, systemId)
  })
}

export const remoteAgentKeys = { all: ['admin', 'a2a', 'agents'] as const }

export const useRemoteAgents = (enabled: boolean) =>
  useQuery({ queryKey: remoteAgentKeys.all, queryFn: fetchRemoteAgents, enabled })

export const useUpdateRemoteAgent = (id: string) => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ version, patch }: { version: number; patch: UpdateRemoteAgentRequest }) =>
      updateRemoteAgent(id, version, patch),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: remoteAgentKeys.all })
    }
  })
}
